import { Deal } from "manager/hooks/application-summary/mappers";
import { VERO_CANCEL_REASON_IDENTITY } from "shared/config/veroCancelReasons";

import { ApplicantSummary } from "../types";

export const applicantFirstName = (
  applicants?: Pick<ApplicantSummary, "firstName">[]
): string => {
  const name = applicants?.[0]?.firstName?.trim();
  return name || "the applicant";
};

export const applicantFullName = (
  applicants?: Pick<ApplicantSummary, "firstName" | "lastName">[]
): string => {
  const first = applicants?.[0];
  const name = [first?.firstName, first?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || "the applicant";
};

export const formatCancelUnitLabel = (unitName?: string): string | undefined => {
  const name = unitName?.trim();
  if (!name) return undefined;
  return /^unit\b/i.test(name) ? name : `Unit ${name}`;
};

export const buildCancelHeaderPlace = ({
  unitName,
  propertyName,
}: {
  unitName?: string;
  propertyName?: string;
}): string => {
  return [formatCancelUnitLabel(unitName), propertyName]
    .filter(Boolean)
    .join(", ");
};

export const buildCancelHeaderMeta = ({
  applicantsSummary,
  unitName,
  propertyName,
}: {
  applicantsSummary?: ApplicantSummary[];
  unitName?: string;
  propertyName?: string;
}): string => {
  const name = applicantFullName(applicantsSummary);
  const place = buildCancelHeaderPlace({ unitName, propertyName });
  return place ? `Applicant: ${name} · ${place}` : `Applicant: ${name}`;
};

const SENTILINK_FINISHED_OUTCOMES = new Set(["PASS", "REVIEW"]);

/** PRODUCT-2537: unfinished line only for not started / blocked / error. */
export const isIdentityCheckUnfinished = (deal?: Deal): boolean => {
  return (deal?.applications ?? []).some((app) => {
    const sentilink = app.sentilink;
    if (!sentilink) return false;
    if (sentilink.identityManuallyVerified) return false;
    if (
      sentilink.outcome &&
      SENTILINK_FINISHED_OUTCOMES.has(sentilink.outcome)
    ) {
      return false;
    }
    if (sentilink.outcome === "BLOCK" || sentilink.outcome === "ERROR") {
      return true;
    }
    if (sentilink.notTriggeredStatus === "SKIPPED_NO_SSN") return false;
    const notStarted =
      sentilink.notTriggeredStatus === "NOT_STARTED" || !sentilink.triggered;
    if (!notStarted) return false;
    // Persona ID already accepted (e.g. Low Risk PASS path) is not unfinished.
    if (app.applicant?.identity?.verified === true) return false;
    return true;
  });
};

export const unfinishedIntroCopy = ({
  firstName,
  yardiConnected,
}: {
  firstName: string;
  yardiConnected: boolean;
}): string => {
  const yardiBit = yardiConnected
    ? ", and writes the cancellation back to Yardi"
    : "";
  return `${firstName} never got past the Identity Risk Check, so their application is unfinished. Cancelling it frees the unit and takes them out of your queue${yardiBit}.`;
};

export const warningCopy = ({ firstName }: { firstName: string }): string => {
  return `This action is permanent and cannot be reversed. ${firstName} will be unable to complete this application, and a replacement requires a new invitation. Notifying the applicant is optional; no email is sent unless you elect to send one.`;
};

/** PRODUCT-2635: BGCC already ran — canned identity copy must not apply. */
export const dealHasBgccRun = (deal?: Deal): boolean => {
  return (deal?.applications ?? []).some((app) =>
    Boolean(app?.backgroundCreditCheckCompleted)
  );
};

/** PRODUCT-2635: same gate as ApplicantDealCancelled* mailers (unit_listing). */
export const identityCancelCannedUsesUnitCopy = (deal?: {
  unitListing?: unknown;
}): boolean => Boolean(deal?.unitListing);

export const isCannedIdentityCancelActive = ({
  flagOn,
  reason,
  bgccAlreadyRan,
}: {
  flagOn: boolean;
  reason?: string;
  bgccAlreadyRan?: boolean;
}): boolean =>
  Boolean(flagOn) &&
  reason === VERO_CANCEL_REASON_IDENTITY &&
  !bgccAlreadyRan;

export const listingUnitName = (deal?: {
  unit?: { name?: string };
  unitListing?: { unit?: { name?: string } | number } | null;
}): string | undefined => {
  const nested = deal?.unitListing?.unit;
  if (nested && typeof nested === "object" && nested.name?.trim()) {
    return nested.name.trim();
  }
  return deal?.unit?.name?.trim() || undefined;
};

/** PRODUCT-2635: latest approved identity-cancel email body (Sept 7 copy). */
export const buildIdentityCancelCannedEmailBody = ({
  propertyName,
  unitNumber,
  companyName,
  useUnitTemplate,
}: {
  propertyName?: string;
  unitNumber?: string;
  companyName?: string;
  /** Same gate as the mailer. Do not infer this from unitNumber alone. */
  useUnitTemplate?: boolean;
}): string => {
  const hasUnit = Boolean(unitNumber?.trim());
  const useUnit = useUnitTemplate ?? (hasUnit && Boolean(propertyName?.trim()));
  const place =
    useUnit && hasUnit && propertyName?.trim()
      ? `${propertyName.trim()} - ${unitNumber.trim()}`
      : useUnit
        ? (propertyName || companyName || "the company").trim()
        : (companyName || propertyName || "the company").trim();
  const office = useUnit
    ? (propertyName || companyName || "the company").trim()
    : (companyName || propertyName || "the company").trim();
  return [
    `The application submitted to ${place} was cancelled because we were unable to verify the identity information provided with your application.`,
    "",
    `This application cannot be reopened. If you would like to submit a new application, please contact the leasing office at ${office}.`,
  ].join("\n");
};
